import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { UserNotesPanel } from "@/components/notes/UserNotesPanel";

interface LeadDrawerContentProps {
  id: number;
}

/**
 * LeadDrawerContent
 * Anteprima rapida di un lead nel RecordDrawer.
 * Dati da /api/leads/:id.
 * Consente modifica rapida dei campi principali e aggiunta note (notes_user).
 */
export default function LeadDrawerContent({ id }: LeadDrawerContentProps) {
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState({ name: "", email: "", phone: "" });

  const { data: lead, isLoading, isError } = useQuery({
    queryKey: ["/api/leads", id],
    queryFn: () => apiRequest(`/api/leads/${id}`),
    enabled: id != null,
  });

  const updateMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      apiRequest("PATCH", `/api/leads/${id}`, payload),
    onSuccess: (updated) => {
      queryClient.setQueryData(["/api/leads", id], (current: any) => ({
        ...(current || {}),
        ...(updated || {}),
      }));
      queryClient.invalidateQueries({ queryKey: ["/api/leads"] });
      queryClient.invalidateQueries({ queryKey: ["/api/emails"] });
    },
    onError: () => {
      toast({ title: "Errore durante il salvataggio", variant: "destructive" });
    },
  });

  if (isLoading) {
    return <div className="p-5 text-muted-foreground">Caricamento...</div>;
  }

  if (isError || !lead) {
    return <div className="p-5 text-destructive">Errore caricamento lead</div>;
  }

  const fullName =
    [lead.firstName, lead.lastName].filter(Boolean).join(" ") ||
    lead.name ||
    "Lead senza nome";
  const email: string | undefined = lead.companyEmail || lead.privateEmail || lead.email;
  const phone: string | undefined = lead.mobilePhone || lead.phone;
  const company: string | undefined = lead.company || lead.companyName;

  const startEditing = () => {
    setDraft({
      name: fullName === "Lead senza nome" ? "" : fullName,
      email: email || "",
      phone: phone || "",
    });
    setIsEditing(true);
  };

  const handleSave = () => {
    // Il nome completo viene diviso su firstName/lastName (entrambi NOT NULL)
    const parts = draft.name.trim().split(/\s+/).filter(Boolean);
    const firstName = parts[0] || lead.firstName || "-";
    const lastName = parts.slice(1).join(" ") || lead.lastName || "-";
    const emailField = lead.privateEmail != null && lead.companyEmail == null ? "privateEmail" : "companyEmail";
    updateMutation.mutate(
      {
        firstName,
        lastName,
        [emailField]: draft.email.trim() || null,
        mobilePhone: draft.phone.trim() || null,
      },
      {
        onSuccess: () => {
          setIsEditing(false);
          toast({ title: "Lead aggiornato" });
        },
      }
    );
  };


  return (
    <div className="flex flex-col">
      <div className="p-5 border-b border-border">
        <h2 className="text-lg font-semibold text-foreground">{fullName}</h2>
        <div className="mt-2 flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(`/leads/${id}`)}
            className="text-sm font-medium text-primary hover:underline"
          >
            Apri scheda completa
          </button>
          {!isEditing && (
            <button
              type="button"
              onClick={startEditing}
              className="text-sm text-muted-foreground hover:text-foreground hover:underline"
            >
              Modifica
            </button>
          )}
        </div>
      </div>

      {lead.status && (
        <div className="p-5 border-b border-border">
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Stato</div>
          <Badge variant="secondary" className="text-xs">{lead.status}</Badge>
        </div>
      )}

      {isEditing ? (
        <div className="p-5 border-b border-border space-y-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Nome</div>
            <Input
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Email</div>
            <Input
              type="email"
              value={draft.email}
              onChange={(e) => setDraft({ ...draft, email: e.target.value })}
            />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Telefono</div>
            <Input
              value={draft.phone}
              onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={updateMutation.isPending}
              className="bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 rounded-lg"
            >
              Salva
            </button>
            <button
              type="button"
              onClick={() => setIsEditing(false)}
              disabled={updateMutation.isPending}
              className="border border-border bg-background px-3 py-1.5 text-sm text-foreground hover:bg-muted rounded-lg"
            >
              Annulla
            </button>
          </div>
        </div>
      ) : (
        <>
          {email && (
            <div className="p-5 border-b border-border">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Email</div>
              <a href={`mailto:${email}`} className="text-primary hover:underline">{email}</a>
            </div>
          )}

          {phone && (
            <div className="p-5 border-b border-border">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Telefono</div>
              <div className="text-foreground">{phone}</div>
            </div>
          )}
        </>
      )}

      {company && (
        <div className="p-5 border-b border-border">
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Azienda</div>
          <div className="text-foreground">{company}</div>
        </div>
      )}

      {(lead.source || lead.leadSource) && (
        <div className="p-5 border-b border-border">
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Fonte</div>
          <div className="text-foreground">{lead.source || lead.leadSource}</div>
        </div>
      )}

      <div className="p-5 border-b border-border">
        <UserNotesPanel entityType="lead" entityId={id} notesUser={lead.notes_user} />
      </div>
    </div>
  );
}
